/**
 * SandboxV2ToolbarButton — the Flow Designer toolbar control for a
 * Sandboxes v2-backed run target. Sits alongside `SandboxToolbarButton` (v1)
 * — the two are mutually exclusive, since an environment is backed by
 * exactly one of a v1 template or a v2 definition environment.
 *
 * Unlike v1, there is no persistent shared instance to warm/stop here: every
 * run claims its own disposable copy of the target commit's ready snapshot,
 * torn down afterward (see `specs/pending/sandboxes-v2-ready-snapshots.md`).
 * This button is informational only — no Start/Stop actions apply.
 *
 * Shows the environment's default-branch readiness (`useSandboxV2DesignerTarget`,
 * backed by `GET /org/sandboxes-v2/environments/:id/readiness`) plus, when a
 * run is live or was last run, that run's own copy phases
 * (`useWorkflowRun`'s `sandboxV2Sandbox`).
 */
import React from 'react';
import { CircleNotch, Cube, WarningCircle } from '@phosphor-icons/react';
import { Button, Popover, PopoverContent, PopoverTrigger, Text } from '@chatsdet/ui';
import { shortSandboxV2Version } from '../../../../hooks/useSandboxV2EnvironmentReadiness';
import type { WorkflowRunSandboxV2Dto } from '../hooks/useWorkflowRun';

export type SandboxV2CurrentRun = WorkflowRunSandboxV2Dto | null;

type ReadinessStatus = 'ready' | 'warming' | 'stale' | 'none';

export interface SandboxV2ToolbarButtonProps {
  environmentName: string;
  environment: string | null;
  isLoading: boolean;
  status: ReadinessStatus | null;
  version: string | null;
  error: string | null;
  currentRun: SandboxV2CurrentRun;
}

const STATUS_ICON_COLOR: Record<ReadinessStatus, string> = {
  ready: 'text-green-600',
  warming: 'text-blue-500',
  stale: 'text-amber-500',
  none: 'text-muted-foreground',
};

const STATUS_LABEL: Record<ReadinessStatus, (version: string | null) => string> = {
  ready: (version) => `Ready at ${shortSandboxV2Version(version)}`,
  warming: () => 'Warming',
  stale: (version) => `No recent snapshot (last ${shortSandboxV2Version(version)})`,
  none: () => 'No snapshot yet',
};

export const SandboxV2ToolbarButton: React.FC<SandboxV2ToolbarButtonProps> = ({
  environmentName,
  environment,
  isLoading,
  status,
  version,
  error,
  currentRun,
}) => {
  if (isLoading) {
    return (
      <Button
        variant="icon"
        size="sm"
        iconOnly
        isDisabled
        tooltip={`Resolving Sandboxes v2 target — ${environmentName}`}
        aria-label="Sandboxes v2 — resolving"
      >
        <CircleNotch size={16} className="animate-spin text-muted-foreground" />
      </Button>
    );
  }

  const resolvedStatus = error ? null : status;
  const label = resolvedStatus
    ? `Sandboxes v2 · ${STATUS_LABEL[resolvedStatus](version)} — ${environmentName}`
    : `Sandboxes v2 unavailable — ${environmentName}`;

  return (
    <Popover placement="bottom">
      <PopoverTrigger>
        <Button variant="icon" size="sm" iconOnly tooltip={label} aria-label={label}>
          {!resolvedStatus ? (
            <WarningCircle size={16} className="text-status-degraded" />
          ) : resolvedStatus === 'warming' ? (
            <CircleNotch size={16} className="animate-spin text-blue-500" />
          ) : (
            <Cube
              size={16}
              weight={resolvedStatus === 'ready' ? 'fill' : 'regular'}
              className={STATUS_ICON_COLOR[resolvedStatus]}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-3 max-w-xs space-y-2">
        <div className="space-y-1">
          <Text size="sm" weight="medium">
            {environmentName} · Sandboxes v2
          </Text>
          <Text size="xs" className="text-muted-foreground">
            {environment ? `Environment ${environment}` : ''}
          </Text>
        </div>
        {!resolvedStatus ? (
          <Text size="sm" className="text-destructive">
            {error ?? 'This environment could not be resolved.'}
          </Text>
        ) : (
          <Text size="xs" className="text-muted-foreground">
            {resolvedStatus === 'ready' && (
              <>
                Target version <code className="text-xs">{shortSandboxV2Version(version)}</code>. Each run claims
                a fresh, disposable copy of it.
              </>
            )}
            {resolvedStatus === 'warming' &&
              "A snapshot is building for this environment's current version."}
            {resolvedStatus === 'stale' && (
              <>
                Last snapshot <code className="text-xs">{shortSandboxV2Version(version)}</code> is too old to
                trust as the current version — the next run builds or claims fresh instead of
                copying it.
              </>
            )}
            {resolvedStatus === 'none' &&
              'No ready snapshot yet — the periodic refresh builds one, or the next run will.'}
          </Text>
        )}
        {currentRun && (
          <div className="space-y-1 border-t border-border pt-2">
            <Text size="xs" weight="medium">
              This run's copy
            </Text>
            <Text size="xs" className="text-muted-foreground">
              Status: {currentRun.status}
            </Text>
            {currentRun.phases ? (
              <Text size="xs" className="text-muted-foreground tabular-nums">
                Snapshot {currentRun.phases.snapshotMs}ms · Restore {currentRun.phases.restoreMs}ms
                · Prepare {currentRun.phases.prepareMs}ms
              </Text>
            ) : (
              <Text size="xs" className="text-muted-foreground">
                Phases not yet available.
              </Text>
            )}
            {currentRun.error && (
              <Text size="xs" className="text-destructive">
                {currentRun.error}
              </Text>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

SandboxV2ToolbarButton.displayName = 'SandboxV2ToolbarButton';
